import React from 'react';
import {View, Text, StyleSheet, ActivityIndicator} from 'react-native';
import {SummaryIncident} from '../../services/diretta';
import {useTheme} from '../../hooks/useTheme';

interface SummaryTabProps {
  summary: Record<string, SummaryIncident[]>;
  loading?: boolean;
}

export const SummaryTab: React.FC<SummaryTabProps> = ({summary, loading}) => {
  const {colors} = useTheme();
  const getIcon = (type: string): string => {
    switch (type) {
      case 'GOAL':
      case 'PENALTY_KICK':
        return '⚽';
      case 'YELLOW_CARD':
        return '🟡';
      case 'RED_CARD':
        return '🔴';
      case 'SUBSTITUTION_OUT':
        return '🔄';
      default:
        return '';
    }
  };

  // Sort stages: 1 TEMPO before 2 TEMPO
  const sortedStages = Object.keys(summary).sort((a, b) => {
    const aNum = a.includes('1') ? 1 : a.includes('2') ? 2 : 3;
    const bNum = b.includes('1') ? 1 : b.includes('2') ? 2 : 3;
    return aNum - bNum;
  });

  // Sort incidents by time within each stage
  const sortIncidents = (incidents: SummaryIncident[]) => {
    return [...incidents].sort((a, b) => {
      const aTime = parseInt((a.incident_time || '0').replace(/[^0-9]/g, ''), 10) || 0;
      const bTime = parseInt((b.incident_time || '0').replace(/[^0-9]/g, ''), 10) || 0;
      return aTime - bTime;
    });
  };

  // Deduplicate incidents by time + team + participants
  const deduplicateIncidents = (incidents: SummaryIncident[]) => {
    const seen = new Set<string>();
    return incidents.filter((incident) => {
      const participantNames = (incident.incident_participants || [])
        .map(p => p.participant_name)
        .sort()
        .join(',');
      const key = `${incident.incident_time}-${incident.incident_team}-${participantNames}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Show loading spinner when loading or when summary is empty (initial load)
  if (loading || (!summary || Object.keys(summary).length === 0)) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={[styles.loadingText, {color: colors.textSecondary}]}>Caricamento riassunto...</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, {backgroundColor: colors.background}]}>
      {sortedStages.map((stageName) => {
        const incidents = deduplicateIncidents(sortIncidents(summary[stageName] || []));
        if (incidents.length === 0) return null;

        return (
          <View key={stageName} style={styles.stageContainer}>
            {/* Stage Header */}
            <View style={[styles.stageHeader, {backgroundColor: '#441274'}]}>
              <Text style={styles.stageHeaderText}>{stageName.toUpperCase()}</Text>
            </View>

            {/* Incidents */}
            {incidents.map((incident, index) => {
              const participants = incident.incident_participants || [];
              const isHome = incident.incident_team === 1;
              const type = participants[0]?.incident_type || '';
              const icon = getIcon(type);

              return (
                <View
                  key={index}
                  style={[
                    styles.incidentRow,
                    {backgroundColor: colors.background, borderBottomColor: colors.border},
                    isHome ? styles.homeRow : styles.awayRow,
                  ]}
                >
                  {isHome ? (
                    <>
                      <Text style={[styles.time, {color: colors.primary}]}>{incident.incident_time}</Text>
                      <Text style={styles.icon}>{icon}</Text>
                      <View style={styles.detail}>
                        {participants.map((p, i) => (
                          <Text
                            key={i}
                            style={p.incident_type === 'ASSISTANCE' 
                              ? [styles.assistName, {color: colors.textSecondary}] 
                              : [styles.playerName, {color: colors.textPrimary}]}
                          >
                            {p.participant_name}{i < participants.length - 1 ? ', ' : ''}
                          </Text>
                        ))}
                      </View>
                    </>
                  ) : (
                    <>
                      <View style={[styles.detail, styles.detailRight]}>
                        {participants.map((p, i) => (
                          <Text
                            key={i}
                            style={p.incident_type === 'ASSISTANCE' 
                              ? [styles.assistName, {color: colors.textSecondary}] 
                              : [styles.playerName, {color: colors.textPrimary}]}
                          >
                            {p.participant_name}{i < participants.length - 1 ? ', ' : ''}
                          </Text>
                        ))}
                      </View>
                      <Text style={styles.icon}>{icon}</Text>
                      <Text style={[styles.time, styles.timeRight, {color: colors.primary}]}>{incident.incident_time}</Text>
                    </>
                  )}
                </View>
              );
            })}
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  stageContainer: {
  },
  stageHeader: {
    paddingVertical: 10,
    alignItems: 'center',
  },
  stageHeaderText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 1,
  },
  incidentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.06)',
  },
  homeRow: {
    justifyContent: 'flex-start',
  },
  awayRow: {
    justifyContent: 'flex-end',
  },
  time: {
    fontSize: 13,
    fontWeight: '600',
    minWidth: 45,
  },
  timeRight: {
    textAlign: 'right',
  },
  icon: {
    fontSize: 14,
    marginHorizontal: 10,
  },
  detail: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    flex: 1,
  },
  detailRight: {
    justifyContent: 'flex-end',
  },
  playerName: {
    fontSize: 13,
    fontWeight: '600',
  },
  assistName: {
    fontSize: 12,
  },
  emptyContainer: {
    padding: 30,
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    margin: 16,
    borderRadius: 12,
  },
  loadingContainer: {
    padding: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
});
